// Backward-compatible facade. Each *Service object is now just a thin alias
// over the per-module API files under `src/api/`. Use either:
//
//   import { userApi } from "@/api/userApi";          // preferred
//   import { UsersService } from "@/services";        // legacy
//
// Both call the same axiosClient under the hood.

import { authApi } from "../api/authApi";
import { userApi } from "../api/userApi";
import { bottleTypeApi } from "../api/bottleTypeApi";
import { companyApi } from "../api/companyApi";
import { productApi } from "../api/productApi";
import { biteCategoryApi } from "../api/biteCategoryApi";
import { biteProductApi } from "../api/biteProductApi";
import { stockApi } from "../api/stockApi";
import { salesApi, cashSessionApi, correctionApi } from "../api/salesApi";
import { reportApi, auditApi, notificationApi } from "../api/reportApi";
import { restaurantStockApi } from "../api/restaurantStockApi";
import { cashSettlementApi } from "../api/cashSettlementApi";
import { restaurantSettlementApi } from "../api/restaurantSettlementApi";
import { notificationCenterApi } from "../api/notificationCenterApi";

export const AuthService = {
  login: (u, p) => authApi.login(u, p),
};

export const UsersService = {
  list: userApi.getAll,
  create: userApi.create,
  update: userApi.update,
  setStatus: userApi.setStatus,
  remove: userApi.remove,
};

export const BottleTypesService = {
  list: bottleTypeApi.getAll,
  create: bottleTypeApi.create,
  update: bottleTypeApi.update,
  setStatus: bottleTypeApi.setStatus,
  remove: bottleTypeApi.remove,
};

export const BottleCompaniesService = { list: companyApi.getAll };

export const BottleProductsService = {
  list: productApi.getAll,
  activeForStockEntry: productApi.getActive,
  create: productApi.create,
  update: productApi.update,
  setStatus: productApi.setStatus,
  uploadImage: productApi.uploadImage,
  removeImage: productApi.removeImage,
};

export const BiteCategoriesService = {
  list: biteCategoryApi.getAll,
  create: biteCategoryApi.create,
  update: biteCategoryApi.update,
  setStatus: biteCategoryApi.setStatus,
  remove: biteCategoryApi.remove,
};

export const BiteProductsService = {
  list: biteProductApi.getAll,
  activeStockTrackedForStockEntry: biteProductApi.getActiveStockTracked,
  create: biteProductApi.create,
  update: biteProductApi.update,
  setStatus: biteProductApi.setStatus,
  getOutletSettings: biteProductApi.getOutletSettings,
  updateOutletSetting: biteProductApi.updateOutletSetting,
  uploadImage: biteProductApi.uploadImage,
  removeImage: biteProductApi.removeImage,
};

export const StockService = {
  journey: stockApi.journey,
  list: stockApi.getAll,
  adjustableBatches: stockApi.getAdjustableBatches,
  create: stockApi.create,
  update: stockApi.update,
  remove: stockApi.remove,
  adjust: stockApi.adjust,
  adjustments: stockApi.listAdjustments,
  updateAdjustment: stockApi.updateAdjustment,
  removeAdjustment: stockApi.removeAdjustment,
  lowStock: stockApi.lowStock,
  bulkCreate: stockApi.bulkCreate,
};

export const RestaurantStockService = {
  list: restaurantStockApi.getAll,
  detail: restaurantStockApi.getById,
  create: restaurantStockApi.create,
  update: restaurantStockApi.update,
  remove: restaurantStockApi.remove,
  lowStock: restaurantStockApi.lowStock,
  bulkCreate: restaurantStockApi.bulkCreate,
};

export const PosService = {
  bottles: salesApi.posBottles,
  bites: salesApi.posBites,
  createSale: salesApi.create,
  saleDetail: salesApi.getById,
  mySales: salesApi.mySales,
  mySalesPage: salesApi.mySalesPage,
  correctionRequest: salesApi.requestCorrection,
  cashCurrent: cashSessionApi.getCurrent,
  cashOpen: cashSessionApi.open,
  cashClose: cashSessionApi.close,
};

export const AdminSalesService = {
  list: (businessDate) => salesApi.adminAll(businessDate),
  detail: (saleId) => salesApi.adminDetail(saleId),
  correctionBites: salesApi.adminCorrectionBites,
  manualCorrection: salesApi.adminManualCorrection,

  cashiersForMissedSale: salesApi.adminCashiersForMissedSale,
  missedSaleBites: salesApi.adminMissedSaleBites,
  createMissedSale: salesApi.adminCreateMissedSale,
};

export const CorrectionsService = {
  list: correctionApi.getAll,
  approve: correctionApi.approve,
  reject: correctionApi.reject,
};

export const ReportsService = reportApi;

export const AuditService = {
  list: auditApi.getAll,
};

export const CashSessionsAdminService = {
  list: (businessDate, outletCode) => cashSessionApi.adminAll(businessDate, outletCode),
};

export const NotificationsService = {
  recipients: notificationApi.getRecipients,
  createRecipient: notificationApi.createRecipient,
  updateRecipient: notificationApi.updateRecipient,
  setRecipientStatus: notificationApi.setRecipientStatus,
  logs: notificationApi.getLogs,
};

export const CashSettlementService = {
  current: cashSettlementApi.current,
  history: cashSettlementApi.history,
  expenses: cashSettlementApi.expenses,
  addExpense: cashSettlementApi.addExpense,
  voidExpense: cashSettlementApi.voidExpense,
  requestSettlement: cashSettlementApi.requestSettlement,

  adminList: cashSettlementApi.adminList,
  confirm: cashSettlementApi.confirm,
  reject: cashSettlementApi.reject,
  reverse: cashSettlementApi.reverse,
};

export const RestaurantSettlementService = {
  list: restaurantSettlementApi.list,
  confirm: restaurantSettlementApi.confirm,
  reject: restaurantSettlementApi.reject,

  current: restaurantSettlementApi.current,
  expenses: restaurantSettlementApi.expenses,
  addExpense: restaurantSettlementApi.addExpense,
  voidExpense: restaurantSettlementApi.voidExpense,
  ownerSettlements: restaurantSettlementApi.ownerSettlements,
  requestOwnerSettlement: restaurantSettlementApi.requestOwnerSettlement,
};

export const NotificationCenterService = notificationCenterApi;
